import { Component, Inject, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FlexibleSelectionFormFieldComponent } from '../../../../../shared/components/form-fields/flexible-selection-form-field/flexible-selection-form-field.component';
import { FormFieldService } from '../../../../../shared/services/form-field.service';
import { CurrentSiteService } from '../../../../../shared/services/current-site.service';
import { ListConstraintColumn } from '../../../../models/flexible-selection-fields/list-constraint-column.model';
import { FlexibleSelectionFieldService } from '../../../../services/flexible-selection-field.service';
import { FormFieldListValuesConstraint } from '../../../../models/form-builder/field-constraints/form-field-list-values-constraint.model';
import { FormField } from '../../../../models/form-builder/form-field.model';
import { FormFieldConstraintTypeEnum } from '../../../../enums/form-field-constraint-type.enum';
import { ListConstraintValue } from '../../../../models/flexible-selection-fields/list-constraint-value.model';
import { AllFieldInfo } from '../../../../models/field-conditional-logic/conditional-logic-editor-classes';
import { FieldDefinitionService } from '../../../../services/field-definition.service';
import { IFieldDefinitionLogic } from '../../../../interfaces/ifield-definition-logic.interface';
import { FieldDefinition } from '../../../../models/form-builder/field-definition.model';
import { Form } from '../../../../models/form-builder/form.model';
import { FormFieldPropertyEnum } from '../../../../models/form-builder/form-field-property-enum.model';
import { StringUtil } from '../../../../utility-classes/string.util';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../../dialogs/confirmation/confirmation-dialog.component';
import { FormFieldListConstraintColumn, FlexibleSelectionFieldConfigurationData } from './extras';

/*
    Note: this component is used as a dialog and is opened from
    - form-builder.component.ts
    - gird-field-designer.component.ts
*/
@Component({
    selector: 'app-flexible-selection-field-configuration',
    templateUrl: './flexible-selection-field-configuration.component.html',
    styleUrls: ['./flexible-selection-field-configuration.component.scss'],
    standalone: false
})
export class FlexibleSelectionFieldConfigurationComponent implements OnInit {

    @Output() save: EventEmitter<FlexibleSelectionFormFieldComponent> = new EventEmitter();

    ngFormGroup: UntypedFormGroup;

    Constraints: FormFieldListValuesConstraint[];
    loadingConstraints: boolean = false;
    Columns: ListConstraintColumn[];
    loadingColumns: boolean = false;
    AvailableDefaultValues: ListConstraintValue[];
    SelectedConstraintId: number;
    SelectedColumnId: number;
    private formField: FormField;
    formFields: FormField[];
    private fieldColumnPairs: FormFieldListConstraintColumn[];
    private sourceFieldConstraints: FormFieldListConstraintColumn[];

    private selectedConstraintIsSimpleList: boolean = true;
    private allFieldInfo: AllFieldInfo;
    private selectedMetadataField: FormField;
    private formTemplateId: number;
    private fieldNameErrorMsg: string;
    private form: Form = new Form(); // Used to check for form field name unique values.
    private fieldInsideGridCol: boolean = false;
    private availableSourceFields: FormField[];

    constructor(private formFieldService: FormFieldService,
        private flexibleSelectionFieldService: FlexibleSelectionFieldService,
        private currentSiteService: CurrentSiteService,
        private fieldDefinitionService: FieldDefinitionService,
        private fb: UntypedFormBuilder,
        public dialog: MatDialog, 
        public dialogRef: MatDialogRef<FlexibleSelectionFieldConfigurationComponent>,
        @Inject(MAT_DIALOG_DATA) public data: FlexibleSelectionFieldConfigurationData) {
    }

    // PJH - 5/13/2025 - made some changes to this component as part of VNEXT-1670
    // The injected data is now of type FlexibleSelectionFieldConfigurationData and has fewer properties - doing as much as possible through its formField property
    ngOnInit(): void {
        this.formField = this.data.formField;

        this.ngFormGroup = this.fb.group({
            name: [this.formField.name],
            id: [this.formField.id],
            displayName: [this.formField.displayName],
            toolTip: [this.formField.toolTip],
            placeholderText: [this.formField.placeholderText],
            instructionsText: [this.formField.instructionsText],
            // NOTE: The hasFootnote flag only has effect on Form-level fields, not on Grid-level fields. The flag is not a property of FormField
            //       but rather, is calculated on the server based on the existence of a child field that is a footnote field. Grid fields don't have child fields
            hasFootnote: this.formField.hasFootnote,
            autoCompleteEnabled: [this.formField.autocomplete],
            maximumSelections: this.formField.maxSelections ? this.formField.maxSelections : 1,
            required: this.formField.required == null ? false : [this.formField.required],
            selectedConstraintName: '',
            selectedConstraintColumnName: '',
            ListConstraintColumnId: [this.formField.ListConstraintColumnId],
            dependsOnParentFormFieldId: [this.formField.dependsOnParentFormFieldId],
            defaultValue: this.formField.defaultValue == null ? "" : [this.formField.defaultValue],
            dependsOnParentFormFieldName: [this.data.dependsOnParentFormFieldName], // I tried removing this but it's needed
            formFields: [this.data.formFields],
            fieldColumnPairs: [this.data.fieldColumnPairs],
            sourceFieldConstraints: [this.data.sourceFieldConstraints]
        });
        this.formTemplateId = this.data.formTemplateId;
        this.formFields = this.data.formFields;
        this.form.formFields = this.formFields;

        //**Set flag which indicates if the flex field is inside of a grid
        if (this.formField != null && this.formField.gridColClientId > 0)
            this.fieldInsideGridCol = true;

        this.loadingConstraints = true;
        this.formFieldService.getSiteNamedListValueConstraints(this.currentSiteService.Site.id).then(constraints => {
            if (constraints) {
                this.Constraints = constraints;
                let selectedConstraint = this.Constraints.find(c => c.constraintName == this.data.listValuesConstraintName);
                if (selectedConstraint) {
                    this.selectedConstraintIsSimpleList = selectedConstraint.constraintType == FormFieldConstraintTypeEnum.SimpleListValues;
                }
                else if (this.data.listValuesConstraintName) {
                    console.log('Constraint not found with value "' + this.data.listValuesConstraintName + '"');
                }
            }
            else {
                console.log('No List Value Constraints returned for site ID ' + this.currentSiteService.Site.id);
            }
            this.loadingConstraints = false;
        });

        this.fieldColumnPairs = [];
        if (this.data.fieldColumnPairs) {
            for (let index: number = 0; index < this.data.fieldColumnPairs.length; index++) {
                let newCol = new FormFieldListConstraintColumn(this.formField.id, this.data.fieldColumnPairs[index].listConstraintColumnId);
                newCol.constraintName = this.data.fieldColumnPairs[index].constraintName;
                newCol.listConstraintColumnName = this.data.fieldColumnPairs[index].columnName;
                newCol.listConstraintColumnId = this.data.fieldColumnPairs[index].listConstraintColumnId;
                this.fieldColumnPairs.push(newCol);
            }
        }

        this.fieldDefinitionService.getMetadataFields().then(metadataFields => {
            this.allFieldInfo = new AllFieldInfo(this.formFields, metadataFields);

            // Filter out fields which are not allowed to be sources of flex field behavior
            // Also filter out the field currently being configured
            this.availableSourceFields = this.allFieldInfo.AllFieldsSortedByNameAscending.filter(formField => {
                let fieldDefClientLogic: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(formField.fieldDefinitionClassName);
                // To add new kind of field to serve as source to a flex field, see https://community-dc.max.gov/x/E4u6mQ
                return fieldDefClientLogic.canParticipateInFlexFieldBehavior &&
                    this.formField.id != formField.id;
                
            });

            // PJH - for VNEXT-1670 - Add UI to allow non-flex fields to be configured as source for a flex field
            this.selectedSourceField = this.availableSourceFields.find(x => { return x.id == this.formField.dependsOnParentFormFieldId });

            this.sourceFieldConstraints = [];
            if (this.data.sourceFieldConstraints) {
                for (let index: number = 0; index < this.data.sourceFieldConstraints.length; index++) {
                    let newCol = new FormFieldListConstraintColumn(this.selectedSourceField.id, this.data.sourceFieldConstraints[index].listConstraintColumnId);
                    newCol.constraintName = this.data.sourceFieldConstraints[index].constraintName;
                    newCol.listConstraintColumnName = this.data.sourceFieldConstraints[index].columnName;
                    newCol.listConstraintColumnId = this.data.sourceFieldConstraints[index].listConstraintColumnId;
                    this.sourceFieldConstraints.push(newCol);
                }
            }
        });
    }

    public get FormField(): FormField {
        return this.formField;
    }

    public get AvailableSourceFields(): FormField[] {
        return this.availableSourceFields;
    }

    public get NgFormGroup(): UntypedFormGroup {
        return (this.ngFormGroup);
    }

    public get LoadingConstraints(): boolean {
        return this.loadingConstraints;
    }
    public get LoadingColumns(): boolean {
        return this.loadingColumns;
    }

    public get FieldColumnPairs(): FormFieldListConstraintColumn[] {
        return this.fieldColumnPairs;
    }

    public get SourceFieldConstraints(): FormFieldListConstraintColumn[] {
        return this.sourceFieldConstraints;
    }

    public get FieldNameErrorMsg(): string {
        return this.fieldNameErrorMsg;
    }
    public get FieldInsideGridCol(): boolean {
        return this.fieldInsideGridCol;
    }

    public cancelClicked() {
        return;
    }

    public onSelectConstraint(selected, nonFlexSourceFormField: FormField = null) {
        let selectedConstraint = this.Constraints.find(c => c.constraintName == selected.value);
        if (selectedConstraint) {
            this.SelectedConstraintId = selectedConstraint.id;
            this.SelectedColumnId = 0;
            this.loadingColumns = true;
            this.flexibleSelectionFieldService.GetColumns(this.SelectedConstraintId).then(res => {
                this.loadingColumns = false;
                this.Columns = res;
                if (selectedConstraint.constraintType == FormFieldConstraintTypeEnum.SimpleListValues) {
                    if (nonFlexSourceFormField != null) {
                        alert("Simple List Value Constraints are not supported on non-Flex Fields. Please choose a cascading list values constraint.");
                        return;
                    }
                    this.ngFormGroup.controls.selectedConstraintName.disable();
                    this.ngFormGroup.controls.selectedConstraintColumnName.disable();
                    this.selectedConstraintIsSimpleList = true;

                    let simpleListCol = this.Columns.filter(c => c.formFieldListValuesConstraintId == selectedConstraint.id)[0];
                    if (simpleListCol) {
                        this.fieldColumnPairs = [];
                        let fcp = new FormFieldListConstraintColumn(this.formField.id, simpleListCol.id);
                        fcp.formFieldName = this.formField.name;
                        fcp.constraintId = this.SelectedConstraintId;
                        fcp.constraintName = selectedConstraint.constraintName;
                        fcp.listConstraintColumnId = simpleListCol.id;
                        fcp.listConstraintColumnName = simpleListCol.name;
                        this.fieldColumnPairs.push(fcp);
                    }
                }
                else {
                    this.ngFormGroup.controls.selectedConstraintName.enable();
                    this.selectedConstraintIsSimpleList = false;
                }
            });
        }
        else {
            console.log('Unable to identify constraint selected as ' + selected.value);
        }
    }

    public onSelectColumn(selected: any, nonFlexSourceFormField: FormField = null) {
        let col = this.Columns.find(c => c.name == selected.value);
        if (col) {
            this.SelectedColumnId = col.id;
            this.addFCpair(nonFlexSourceFormField);
            this.ngFormGroup.controls['selectedConstraintName'].setValue(null);
            this.ngFormGroup.controls['selectedConstraintColumnName'].setValue(null);
        }
        else {
            console.log('Column not found for selected value ' + selected.value);
        }
    }

    public get SourceFieldIsNotFlexField(): boolean {
        return this.selectedSourceField == null ? false : this.selectedSourceField.fieldDefinitionClassName != "Collect.vNext.FieldDefinitions.FlexibleSelectionFieldDefinition";
    }
    public get SelectedSourceField(): FormField {
        return this.selectedSourceField;
    }

    private selectedSourceField: FormField;
    public onSelectSourceField(selected): void {
        let selectedSourceField = this.availableSourceFields.find(x => x.name == selected.value);
        this.selectedSourceField = selectedSourceField;

        if (!selectedSourceField) return;

        let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(selectedSourceField.fieldDefinitionClassName);

        if (fieldDef.isMetadataField && selectedSourceField.id <= 0) {
            if (this.formTemplateId <= 0) {
                alert("In order to set a metadata field, you first need to save the new form.");
                this.ngFormGroup.controls['dependsOnParentFormFieldName'].setValue(null);
                return;
            }
            let metadataFormField: FormField = new FormField();
            metadataFormField.formTemplateId = this.formTemplateId;
            metadataFormField.isMetadataField = true;
            metadataFormField.name = selectedSourceField.fieldDefinitionClassName;
            metadataFormField.displayName = selectedSourceField.displayName;
            metadataFormField.fieldDefinitionClassName = selectedSourceField.fieldDefinitionClassName;
            this.formFieldService.getOrCreateMetaDataField(metadataFormField).then(x => {
                this.selectedMetadataField = x;
                this.selectedSourceField.id = x.id; // set the Id of the metadata field
            });
        }
    }

    public deleteFCpair(FCpair: FormFieldListConstraintColumn, sourceFormField: FormField = null) {
        if (sourceFormField) {
            this.sourceFieldConstraints = this.sourceFieldConstraints.filter(fcp => !(fcp.listConstraintColumnId == FCpair.listConstraintColumnId && fcp.constraintId == FCpair.constraintId));
        } else {
            this.fieldColumnPairs = this.fieldColumnPairs.filter(fcp => !(fcp.listConstraintColumnId == FCpair.listConstraintColumnId && fcp.constraintId == FCpair.constraintId));
        }
    }

    public addFCpair(nonFlexSourceFormField: FormField) {
        const formFieldId = nonFlexSourceFormField ? nonFlexSourceFormField.id : this.formField.id;
        const formFieldName = nonFlexSourceFormField ? nonFlexSourceFormField.name : this.formField.name;

        // Prevent adding duplicates
        if (nonFlexSourceFormField) {
            if (this.sourceFieldConstraints.find(fcp => fcp.constraintId == this.SelectedConstraintId && fcp.listConstraintColumnId == this.SelectedColumnId)) return;
        } else {
            if (this.fieldColumnPairs.find(fcp => fcp.constraintId == this.SelectedConstraintId && fcp.listConstraintColumnId == this.SelectedColumnId)) return;
        }
        let fcp = new FormFieldListConstraintColumn(formFieldId, this.SelectedColumnId);
        fcp.formFieldName = formFieldName;
        fcp.constraintId = this.SelectedConstraintId;
        fcp.constraintName = this.ngFormGroup.value.selectedConstraintName;
        fcp.listConstraintColumnId = this.SelectedColumnId;
        fcp.listConstraintColumnName = this.ngFormGroup.value.selectedConstraintColumnName;

        if (nonFlexSourceFormField) {
            this.sourceFieldConstraints.push(fcp);
        } else {
            this.fieldColumnPairs.push(fcp);
        }
    }

    public onSave() {
        if (this.SourceFieldIsNotFlexField && this.sourceFieldConstraints.length == 0) {
            let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                data: new ConfirmationDialogModel(
                    `Incomplete Configuration`,
                    `You have selected a non-Flex field as the source for this field, but have not configured it.
                    This field won't populate with options. Are you sure you want to save it?`
                )
            });
            dialogRef.disableClose = true;
            dialogRef.afterClosed().subscribe(yes => {
                if (yes) {
                    this.doOnClose();
                }
            });
        } else {
            this.doOnClose();
        }
    }

    private doOnClose() {
        let formVals = this.ngFormGroup.value;
        
        let sourceField = this.availableSourceFields.find(f => f.name == formVals.dependsOnParentFormFieldName);
        let returnValues = {
            form: this.form,
            formFields: this.formFields,
            name: formVals.name,
            constraintIsSimpleList: this.selectedConstraintIsSimpleList,
            dependsOnParentFormFieldDefinitionClassName: sourceField?.fieldDefinitionClassName,
            fieldColumnPairs: this.fieldColumnPairs,
            sourceFieldConstraints: this.sourceFieldConstraints,
            sourceFieldIsNotFlexField: this.SourceFieldIsNotFlexField,
            selectedMetadataField: this.selectedMetadataField,
            id: formVals.id
        };

        // PJH - 5/13/2025 - do as much updating of the formField's properties here, so it doesn't have to be done in the calling code
        //       NOTE: Not setting the name property here because calling code needs to perform specific logic
        this.formField.displayName = formVals.displayName;
        this.formField.maxSelections = formVals.maximumSelections;
        this.formField.required = formVals.required;
        this.formField.defaultValue = formVals.defaultValue;
        this.formField.autocomplete = formVals.autoCompleteEnabled;
        this.formField.toolTip = formVals.toolTip;
        this.formField.placeholderText = formVals.placeHolderText;
        this.formField.instructionsText = formVals.instructionsText;
        this.formField.hasFootnote = formVals.hasFootnote;
        this.formField.ListConstraintColumnId = this.SelectedColumnId;
        this.formField.dependsOnParentFormFieldId = sourceField?.id;
        this.formField.dependsOnParentFormFieldName = sourceField?.name;

        this.dialogRef.close(returnValues);
    }

    public handleModelChange(propertyName: string): void {
        let formVals = this.ngFormGroup.value;
        let internalName = formVals.name;

        if (propertyName === FormFieldPropertyEnum.NAME) {
            this.fieldNameErrorMsg = null;
            if (StringUtil.isEmptyString(internalName)) {
                this.fieldNameErrorMsg = "A field's 'Name' cannot be blank";
            } else if (this.formField.name != internalName && this.form.nameForFormFieldNotUnique(this.formField, internalName)) {
                this.fieldNameErrorMsg = "The field's 'Name' value is not unique";
            } else if (internalName.includes(' ')) {
                this.fieldNameErrorMsg = 'A field internal name cannot contain spaces';
            } else if (internalName != internalName.toLowerCase()) {
                this.fieldNameErrorMsg = 'A field internal name cannot contain capital letters';
            } else if (this.fieldInsideGridCol) {
                //***Perform additional validation if field is inside of a grid field
                if (!isNaN(Number(internalName.charAt(0)))) {
                    this.fieldNameErrorMsg = 'A grid column internal name cannot start with a digit';
                } else if (!(new RegExp('^[a-zA-Z0-9$_]*$').test(internalName))) {
                    this.fieldNameErrorMsg = 'A grid column internal name can contain only letters, numbers, $ and _ symbols';
                }
            }
        }
        //***Leaving these comments for now since the autocomplete logic will be implemenented in the future*/
        //else if (propertyName == FormFieldPropertyEnum.AUTOCOMPLETE) {
        //    if (this.field.autocomplete) {
        //        this.field.autocomplete_StartsWith = true;
        //        this.field.autocomplete_Contains = true;
        //    }
        //}

        //***This logic was used when editing properties via the drawer. Don't think its needed anymore
        // Note: notify my parent that a property has changed.
        //let hshEventData = {
        //    propertyName: propertyName,
        //    formField: this.formField,
        //    cancelEdit: propertyName == FormFieldPropertyEnum.CANCEL_ALL
        //}
        //this.propertyChanged.emit(hshEventData);

        return;
    }
}
